// dependencies
import { useApolloClient } from "@apollo/client";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

// icons
import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";
import { faPen, faSave } from "@fortawesome/pro-solid-svg-icons";
import { faShare } from "@fortawesome/pro-solid-svg-icons";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Alert, AvatarProfile, DropDownMenu, type DropDownMenuOption } from "@performyard/py-components";

// components
import Badge from "../../../../../../components/Badge";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import Button from "../../../../../../components/Button";
import Chip from "../../../../../../components/Chip";
import { Grid, GridCol } from "../../../../../../components/Grid";
import Icon from "../../../../../../components/Icon";
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import Spacer from "../../../../../../components/Spacer";
import Typography from "../../../../../../components/Text";
import ReportInfoModal, { ModalInfo } from "../../../../components/ReportInfoModal";
import ShareReportModal from "../../../Home/components/ShareReportModal";

import { FieldFilter } from "../../../../../ReportAnalytics/schemas/report";

// hooks
import useReportStore from "../../store";
import { ChartType } from "../../store/enums";

import { useRbacPrimaryRolePermissions } from "../../../../../../../react/api/hooks/defs/rbac";
import { useFeatureFlag, useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";
import { useCsvDownload } from "../../../../hooks/useCSVDownload";
import { DELETE_SAVED_QUERY } from "../../../../hooks/useQuery";

import { DisplayConfirmationModal } from "../../../../../../actions/modals.actions";

// utils
import {
	getReportTypeSpecificPermissions,
	getShouldDisplayReviewsInProgressMessage,
	getUserSpecificCompanySettingDependentPermissions,
} from "../../utils";

import { reportTypeLookup } from "../../../../config";
// styles
import css from "./ReportHeader.module.scss";

interface BaseProps {
	saveReport: () => void;
	onSaveAs: (modalInfo: ModalInfo) => void;
	handleToggleFilterSlideout: () => void;
	updatePagination: (pagination: { pageNumber: number; pageSize: number }) => void;
	pageSize: number;
	pageSizes: number[];
}

const ReportHeader = (props: BaseProps) => {
	const client = useApolloClient();

	// props
	const { saveReport, onSaveAs, handleToggleFilterSlideout, updatePagination, pageSize, pageSizes } = props;
	// store
	const [
		report,
		reportID,
		setReportFields,
		activeModal,
		setActiveModal,
		canViewSummary,
		csvJobID,
		resetCSVJobID,
		fetchReportCSV,
		appliedFiltersCount,
		selectedChart,
		getAppliedFilters,
		selectedQuickFilter,
		fetchEmployeeReportCSV,
		fetchGoalReportCSV,
		fetchReport,
		fetchSurveyReportCSV,
	] = useReportStore((state) => [
		state.report,
		state.reportID,
		state.setReportFields,
		state.activeModal,
		state.setActiveModal,
		state.canViewSummary,
		state.csvJobID,
		state.resetCSVJobID,
		state.fetchReportCSV,
		state.appliedFiltersCount,
		state.selectedChart,
		state.getAppliedFilters,
		state.selectedQuickFilter,
		state.fetchEmployeeReportCSV,
		state.fetchGoalReportCSV,
		state.fetchReport,
		state.fetchSurveyReportCSV,
	]);

	const filters: FieldFilter[][] = useMemo(() => {
		return getAppliedFilters();
	}, [selectedQuickFilter, JSON.stringify(report)]);

	const [shareReportId, setShareReportId] = useState(null);
	const [sharedWithManagersOrContributors, setSharedWithManagersOrContributors] = useState<string[]>([]);
	const [reportDetailsExpanded, setReportDetailedExpanded] = useState(false);
	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");

	const goalReportsEnabled = useFeatureFlag<boolean>("ff-is-goals-enabled", true);
	const { managerPermissions, contributorPermissions } = getUserSpecificCompanySettingDependentPermissions();
	const { value: sharingSurveyReportsFF } = useFeatureFlagQuery("ff-temp-manager-contributor-surveyreports", false);

	const hasManagerAccess =
		managerPermissions.canViewGoalReports ||
		managerPermissions.canViewReviewReports ||
		managerPermissions.canViewEmployeeReports ||
		managerPermissions.canViewSurveyReports;
	const hasContributorAccess =
		contributorPermissions.canViewGoalReports ||
		contributorPermissions.canViewReviewReports ||
		contributorPermissions.canViewEmployeeReports ||
		contributorPermissions.canViewSurveyReports;

	const { permissionToCreateReviewReport, permissionToCreateEmployeeReport } =
		getReportTypeSpecificPermissions(permissions);

	const adminPermissions =
		permissionToCreateReviewReport || (permissionToCreateEmployeeReport && report?.type === "employeeReport");

	const permissionToViewSharedWithReports = permissions?.length
		? hasManagerAccess || hasContributorAccess
		: undefined;

	const getCsvData = async () => {
		if (report?.type === "employeeReport") {
			await fetchEmployeeReportCSV();
		} else if (report?.type === "goalReport") {
			await fetchGoalReportCSV();
		} else if (report?.type === "surveyReport") {
			await fetchSurveyReportCSV();
		} else {
			await fetchReportCSV();
		}
	};

	// dependent on csvJobID changes (csvJobID is set in fetchReport above)
	useCsvDownload(csvJobID, resetCSVJobID);

	const handleUpdateReportInfo = (modalInfo: ModalInfo) => {
		setReportFields([
			{ field: "name", value: modalInfo.name },
			{ field: "tags", value: modalInfo.tags },
			{ field: "description", value: modalInfo.description },
			{ field: "sharedWith", value: modalInfo.sharedWith },
			{ field: "pageSize", value: modalInfo.pageSize },
		]);
		setActiveModal(null);
	};

	const handleSaveReportAs = (modalInfo: ModalInfo) => {
		onSaveAs(modalInfo);
		setActiveModal(null);
	};

	const handleSharedWithChange = (newSharedWithManagers: string[]) => {
		setSharedWithManagersOrContributors(newSharedWithManagers);
		fetchReport();
	};

	const handleDeleteReport = async (reportID: string) => {
		await client.mutate({
			mutation: DELETE_SAVED_QUERY,
			variables: {
				reportID,
			},
		});
		// redirect back to report & analytics homepage
		window.location.pathname = "/analytics";
	};
	const dispatch = useDispatch();

	// Options Drawer switch to handle delete and csv download
	const handleDropDownSelect = (key: string) => {
		const sharedWith = report?.["sharedWith"] || ["admins"];
		if (reportID) {
			switch (key) {
				case "DeleteReport": {
					dispatch(
						DisplayConfirmationModal("Are you sure you want to delete this report?", () => {
							handleDeleteReport(reportID);
						}) as any
					);
					break;
				}
				case "EditReport": {
					setActiveModal("EDIT");
					break;
				}
				case "ShareReport": {
					setShareReportId(reportID);
					setSharedWithManagersOrContributors(sharedWith);
					break;
				}
				case "DownloadCSV": {
					getCsvData();
					break;
				}
				default:
					break;
			}
		}
	};

	const handleOpenReportDetails = () => {
		setReportDetailedExpanded(!reportDetailsExpanded);
		return;
	};

	const handleModalAction = (modalInfo: ModalInfo) => {
		if (activeModal === "SAVE_AS") {
			handleSaveReportAs(modalInfo);
		} else if (activeModal === "EDIT") {
			handleUpdateReportInfo(modalInfo);
		}
	};

	const displayReportIsActiveBanner = getShouldDisplayReviewsInProgressMessage(
		permissionToCreateReviewReport,
		filters,
		report?.type
	);
	const breadcrumbName = (report?.name?.length || 0) > 30 ? `${report?.name.slice(0, 30)}...` : report?.name || "";

	const readOnlyActions = {
		key: "DownloadCSV",
		label: "Download CSV",
		right: <Icon size="small" icon={faDownload} />,
	};

	const writeActions = [
		{
			key: "EditReport",
			label: "Edit Report Details",
			right: <Icon size="small" icon={faPen} />,
		},
		{
			key: "DownloadCSV",
			label: "Download CSV",
			right: <Icon size="small" icon={faDownload} />,
		},
		{
			key: "ShareReport",
			label: "Share Report",
			right: <Icon size="small" icon={faShare} />,
		},
		{
			key: "DeleteReport",
			label: "Delete Report",
			right: <Icon size="small" icon={faTrashCan} />,
		},
	];

	let actionsDropDown: DropDownMenuOption[] = [];

	if (adminPermissions) {
		actionsDropDown = [...writeActions];
		// Remove Share Report if conditions aren't met
		if (!permissionToViewSharedWithReports) {
			actionsDropDown = actionsDropDown.filter((action) => action.key !== "ShareReport");
		}
	} else if (selectedChart === ChartType.Table) {
		actionsDropDown.push(readOnlyActions);
	}

	const enabledSharedWithTagOptions = [
		hasManagerAccess && "managers",
		hasContributorAccess && "contributors",
	]?.filter(Boolean) as string[];

	const enabledSharedWithTags = report?.["sharedWith"]?.filter((role: string) =>
		enabledSharedWithTagOptions?.includes(role)
	);

	return (
		<>
			<Breadcrumbs
				pages={[
					{
						pageID: "reportingHome",
						label: "Reporting & Analytics",
						url: "/analytics",
					},
					{
						pageID: reportID || "",
						label: breadcrumbName,
						url: `/analytics/report/${reportID}`,
					},
				]}
				className={css.breadcrumbs}
				forceHardReload
			/>
			<Spacer height={3} />

			{report ? (
				<Grid className={css.header} data-testid="report-header" gap={1} cols={6}>
					<GridCol span={5} className={css.actionColumn}>
						<div className={css.title}>
							<Typography size="heading-5" weight="regular">
								{report.name}
							</Typography>
						</div>
						<div className={`${css.subtitleContainer}`} data-testid="report-details">
							<button
								onClick={handleOpenReportDetails}
								className={css.subtitle}
								style={{ background: "none", border: "none", padding: 0 }}
							>
								<Typography size="paragraph-3" weight="regular">
									Report Details
								</Typography>
							</button>
							<div
								className={css.arrowIcon}
								data-testid={`report-details-expand-arrow-${reportDetailsExpanded ? "up" : "down"}`}
								onClick={handleOpenReportDetails}
							>
								<FontAwesomeIcon icon={reportDetailsExpanded ? faAngleUp : faAngleDown} size={"sm"} />
							</div>
						</div>
					</GridCol>
					<GridCol span={1} className={`${css.actionColumn} ${css.rightCol}`}>
						<div className={css.buttonsContainer} data-testid="save">
							{adminPermissions && (
								<div className={css.buttonGroup} data-testid="save-buttons">
									<Button
										onClick={saveReport}
										className={css.button}
										icon={faSave}
										color="green"
										testid="save-button"
									>
										Save
									</Button>
									<Button
										className={css.button}
										onClick={() => setActiveModal("SAVE_AS")}
										icon={faSave}
										testid="save-as-button"
									>
										Save As
									</Button>
								</div>
							)}
							{actionsDropDown?.length ? (
								<DropDownMenu
									options={actionsDropDown}
									onSelect={handleDropDownSelect}
									minWidth={170}
									placement="bottom-end"
									bordered
								>
									<Icon icon={faEllipsis} size="small" />
								</DropDownMenu>
							) : null}
						</div>
					</GridCol>
					<GridCol span={6} className={css.actionColumn}>
						{!reportDetailsExpanded && (
							<div className={css.reportDetailsTopBorder} data-testid="report-details-top-border" />
						)}
						{reportDetailsExpanded && (
							<div className={css.reportDetailsContainer} data-testid="report-details-container">
								{report?.description ? (
									<div className={css.infoItemDescription}>
										<div className={css.frame} data-testid="report-description">
											<Typography size="paragraph-3" weight="semi-bold">
												Description:
											</Typography>
										</div>
										<div>
											<Typography size="paragraph-3" weight="regular" color={"neutral-7"}>
												{report.description}
											</Typography>
										</div>
									</div>
								) : null}
								<div className={css.infoItem} data-testid="report-created-by">
									<div className={css.frame}>
										<Typography size="paragraph-3" weight="semi-bold">
											Created By:
										</Typography>
										{report.createdBy?._id !== undefined ? (
											<AvatarProfile
												dangerouslySetClassName="noPadding"
												employeeId={report.createdBy._id}
												employeeName={report.createdBy.name}
												avatarSize={16}
												textSize="caption"
											/>
										) : (
											<Typography
												size="paragraph-3"
												weight="regular"
												data-testid="report-created-by-not-found"
											>
												Not Found
											</Typography>
										)}
									</div>
								</div>
								<div className={css.infoItem} data-testid="created-at">
									<div className={css.frame}>
										<Typography size="paragraph-3" weight="semi-bold">
											Created At:
										</Typography>
										<Typography size="paragraph-3" weight="regular">
											{format(report.createdAt, "MMM do, yyyy")}
										</Typography>
										•
										<Typography size="paragraph-3" weight="regular">
											{format(report.createdAt, "h:mm aaa")}
										</Typography>
									</div>
								</div>
								<div className={css.infoItem} data-testid="last-updated">
									<div className={css.frame}>
										<Typography size="paragraph-3" weight="semi-bold">
											Last Updated:
										</Typography>
										<Typography size="paragraph-3" weight="regular">
											{format(report.updatedAt, "MMM do, yyyy")}
										</Typography>
										•
										<Typography size="paragraph-3" weight="regular">
											{format(report.updatedAt, "h:mm aaa")}
										</Typography>
									</div>
								</div>
								{(permissionToViewSharedWithReports &&
									enabledSharedWithTags &&
									enabledSharedWithTags?.length) ||
								report?.tags?.length ? (
									<>
										{permissionToViewSharedWithReports &&
										report["sharedWith"]?.length &&
										(report?.type !== "surveyReport" || sharingSurveyReportsFF) ? (
											<div className={css.infoItem}>
												<div className={css.frame}>
													<Typography size="paragraph-3" weight="semi-bold">
														Shared with:
													</Typography>
													{enabledSharedWithTags?.map((role: string, index) => (
														<Chip
															key={`${role}-${index}`}
															label={
																<Typography size="caption" weight="semi-bold">
																	{role.charAt(0).toUpperCase() + role.slice(1)}
																</Typography>
															}
															color="green"
														/>
													))}
												</div>
											</div>
										) : null}
										{report.tags.length ? (
											<div className={css.infoItem}>
												<div className={css.frameDynamicHeight}>
													<Typography size="paragraph-3" weight="semi-bold">
														Tags:
													</Typography>
													{report.tags.map((tag) => (
														<div className={css.tagChip} key={tag}>
															<Chip
																label={
																	<Typography size="caption" weight="semi-bold">
																		{tag}
																	</Typography>
																}
																color="blue"
															/>
														</div>
													))}
												</div>
											</div>
										) : null}
									</>
								) : null}
							</div>
						)}
					</GridCol>
					<GridCol span={3} className={css.actionRow}>
						{shareReportId && adminPermissions && permissionToViewSharedWithReports ? (
							<ShareReportModal
								handleClose={() => setShareReportId(null)}
								report_id={shareReportId}
								isOpen={!!shareReportId}
								sharedWithWho={sharedWithManagersOrContributors}
								onSharedWithChange={(newSharedWith) => handleSharedWithChange(newSharedWith)}
								managersViewReportsSettingEnabled={Boolean(hasManagerAccess)}
								contributorsViewReportSettingEnabled={Boolean(hasContributorAccess)}
							/>
						) : null}
						<div>
							<div className="w-fit">
								<Badge
									title={`${appliedFiltersCount} filter${appliedFiltersCount === 1 ? "" : "s"} applied`}
									count={appliedFiltersCount}
									overflowCount={9}
								>
									<Button isHollow={true} icon={faBarsFilter} onClick={handleToggleFilterSlideout}>
										{reportTypeLookup[report.type] || ""} Filters
									</Button>
								</Badge>
							</div>
							{filters?.length &&
							filters.some((filter) => {
								return filter.some((f) => f.field === "Activation Status" && f.value === "Deactivated");
							}) ? (
								<div className="my-2">
									<Alert message={"Results may include deactivated employee data"} type="neutral" />
								</div>
							) : null}
						</div>
					</GridCol>
					{selectedChart === ChartType.Table ? (
						<GridCol span={3} className={`${css.actionRow} ${css.rightCol}`}>
							<Typography size="paragraph-3" weight="regular">
								Display Rows:
							</Typography>
							<div className={css.selectInputContainer}>
								<SelectInput
									isSearchable={false}
									value={pageSize.toString()}
									results={pageSizes.map((size) => ({
										label: size.toString(),
										value: size.toString(),
									}))}
									onChange={(val) => {
										updatePagination({ pageNumber: 1, pageSize: val.value });
									}}
								/>
							</div>
						</GridCol>
					) : null}
					{displayReportIsActiveBanner && canViewSummary !== undefined ? (
						<GridCol span={3} className={`${css.actionRow} ${css.leftCol}`}>
							<Typography size="paragraph-3" weight="semi-bold" color="neutral-7">
								<FontAwesomeIcon icon={faInfoCircle} color="neutral-7" style={{ marginRight: "4px" }} />
								Results may include review forms still in progress
							</Typography>
						</GridCol>
					) : null}
				</Grid>
			) : null}
			<Spacer height={1} />
			{report && (activeModal === "EDIT" || activeModal === "SAVE_AS") ? (
				<ReportInfoModal
					title={activeModal === "EDIT" ? "Edit Report" : "Save Report As"}
					handleClose={() => setActiveModal(null)}
					actionText={activeModal === "EDIT" ? "Update" : "Save"}
					handleAction={(modalInfo) => handleModalAction(modalInfo)}
					info={{
						name: report.name,
						description: report.description,
						tags: report.tags,
						type: report.type,
						sharedWith: report["sharedWith"] || [],
						pageSize: report?.pageSize || 50,
					}}
					actionColor="gray-hollow"
					availableFields={["name", "description", "tags"]}
					goalReportsEnabled={goalReportsEnabled}
				/>
			) : null}
		</>
	);
};

export default ReportHeader;
